/**
 * MapControls — floating glass-styled zoom & undo/redo control panel.
 * Positioned at the bottom-right of the map container.
 */

import { ZoomIn, ZoomOut, Maximize2, RotateCcw, Undo2, Redo2, Upload, Download, FolderUp, Plus } from 'lucide-react';

interface MapControlsProps {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onFitToContent: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onAddNode?: () => void;
  onImport?: () => void;
  onExport?: () => void;
  onImportData?: () => void;
}

export function MapControls({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onFitToContent,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onAddNode,
  onImport,
  onExport,
  onImportData,
}: MapControlsProps) {
  const zoomPct = Math.round(zoomLevel * 100);

  return (
    <div className="absolute bottom-3 right-3 flex flex-col items-center gap-1 glass-panel p-1">
      {onAddNode && (
        <>
          <ControlButton icon={<Plus size={16} />} label="Add node" onClick={onAddNode} />
          <div className="w-6 my-0.5" style={{ borderTop: '1px solid var(--glass-border)' }} />
        </>
      )}
      <ControlButton icon={<Undo2 size={16} />} label="Undo (Ctrl+Z)" onClick={onUndo} disabled={!canUndo} />
      <ControlButton icon={<Redo2 size={16} />} label="Redo (Ctrl+Y)" onClick={onRedo} disabled={!canRedo} />
      <div className="w-6 my-0.5" style={{ borderTop: '1px solid var(--glass-border)' }} />
      <ControlButton icon={<ZoomIn size={16} />} label="Zoom in" onClick={onZoomIn} />
      <span
        className="text-[10px] font-medium tabular-nums w-10 text-center"
        style={{ color: 'var(--text-muted)' }}
      >
        {zoomPct}%
      </span>
      <ControlButton icon={<ZoomOut size={16} />} label="Zoom out" onClick={onZoomOut} />
      <div className="w-6 my-0.5" style={{ borderTop: '1px solid var(--glass-border)' }} />
      <ControlButton icon={<Maximize2 size={16} />} label="Fit to content" onClick={onFitToContent} />
      <ControlButton icon={<RotateCcw size={16} />} label="Reset zoom" onClick={onResetZoom} />
      {onImport && (
        <>
          <div className="w-6 my-0.5" style={{ borderTop: '1px solid var(--glass-border)' }} />
          <ControlButton icon={<Upload size={16} />} label="Bulk import (CSV/JSON)" onClick={onImport} />
        </>
      )}
      {(onExport || onImportData) && (
        <>
          <div className="w-6 my-0.5" style={{ borderTop: '1px solid var(--glass-border)' }} />
          {onExport && <ControlButton icon={<Download size={16} />} label="Export data (JSON)" onClick={onExport} />}
          {onImportData && <ControlButton icon={<FolderUp size={16} />} label="Import data (JSON)" onClick={onImportData} />}
        </>
      )}
    </div>
  );
}

function ControlButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      disabled={disabled}
      className="w-8 h-8 flex items-center justify-center rounded transition-colors"
      style={{ color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)', opacity: disabled ? 0.4 : 1 }}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--glass-bg-elevated)'; } }}
      onMouseLeave={(e) => { e.currentTarget.style.color = disabled ? 'var(--text-muted)' : 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
    >
      {icon}
    </button>
  );
}
